/**
 * PortalInviteDocuments — documents page for an individual invite.
 * Shows any site/induction documents from the depot's induction brief.
 */

import React, { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { PageHeader, SectionCard, EmptyState, InviteTabBar } from '@/components/portal/PortalUI';
import { FileText, Download, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@shared/supabaseClient';

const PortalInviteDocuments = () => {
  const { submissionId } = useParams();
  const { session, contextInduction } = useOutletContext();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depotName, setDepotName] = useState(null);

  useEffect(() => {
    loadDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, session]);

  const loadDocuments = async () => {
    if (!submissionId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Get the depot linked to this submission
      const { data: sub, error: subErr } = await supabase
        .from('induction_submissions')
        .select('depot_id, depots:depot_id(id, name)')
        .eq('id', submissionId)
        .maybeSingle();

      if (subErr || !sub) throw new Error('Invite not found');
      setDepotName(sub.depots?.name || null);

      // Look for any file-type induction briefs on this depot
      const { data: briefs } = await supabase
        .from('induction_briefs')
        .select('id, title, brief_type, file_url, updated_at')
        .eq('depot_id', sub.depot_id)
        .eq('is_active', true)
        .in('brief_type', ['pdf', 'video', 'file'])
        .order('updated_at', { ascending: false });

      setDocuments(briefs || []);
    } catch (err) {
      console.error('Error loading invite documents:', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
        <PageHeader title="Documents" subtitle="Site documents and files" />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
      <PageHeader
        title="Documents"
        subtitle={depotName ? `Site documents for ${depotName}` : 'Site documents and files'}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {documents.length > 0 ? (
        <SectionCard title="Available Documents" subtitle={`${documents.length} document${documents.length !== 1 ? 's' : ''}`}>
          <div className="divide-y divide-border">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{doc.brief_type}</p>
                </div>
                {doc.file_url && (
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline shrink-0"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open
                  </a>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      ) : (
        <EmptyState
          icon={FileText}
          title="No documents"
          description="No site documents have been uploaded for this invite. The site team may add documents here in future."
        />
      )}
    </div>
  );
};

export default PortalInviteDocuments;
